import { JsonDecoder } from 'ts.data.json';
import { distributionsDecoder } from '../distributions/decoder';
import { Distributions } from '../distributions/model';
import { fundingInstructionDecoder } from '../funding-instructions/decoder';
import { FundingInstructions } from '../funding-instructions/model';
import { investorDecoder } from '../investor/decoder';
import { Investor } from '../investor/model';
import {
    Account,
    AccountChangeHistoryEntry,
    AccountChangeType,
    AccountContactCreatedAck,
    AccountContactRemovedAck,
    AccountCustomFields,
    AccountDocument,
    AccountEditAck,
    AccountFieldChange,
    AccountOwnershipType,
    AccountDocumentPayload,
    AccountDocumentsFilter,
    AccountDocumentType,
    AccountOrdersFilter,
    AccountPosition,
    AccountPositionDisplayField,
    AccountPositionFootnote,
    AccountRecord,
    AccountSecurity
} from './model';
import { AccountInvestment } from '../registrations/model';
import { AccountInvestmentDecoder } from '../registrations/decoder';
import { filterListDecoder } from '../filters/decoders';

// Account

export const accountPositionFootnoteDecoder = JsonDecoder.object<AccountPositionFootnote>(
    {
        symbol: JsonDecoder.string,
        text: JsonDecoder.string
    },
    'accountPositionFootnote'
);

export const accountPositionDisplayFieldDecoder = JsonDecoder.object<AccountPositionDisplayField>(
    {
        dataType: JsonDecoder.string,
        displayName: JsonDecoder.string,
        footnotes: JsonDecoder.array(accountPositionFootnoteDecoder, 'AccountPositionFootnote[]'),
        headerName: JsonDecoder.string,
        sequence: JsonDecoder.number,
        value: JsonDecoder.string
    },
    'accountPositionDisplayField'
);

export const accountPositionDecoder = JsonDecoder.object<AccountPosition>(
    {
        accountId: JsonDecoder.optional(JsonDecoder.string),
        canViewTransaction: JsonDecoder.optional(JsonDecoder.boolean),
        currentValueFootnote: JsonDecoder.optional(JsonDecoder.string),
        cusip: JsonDecoder.string,
        dateClosed: JsonDecoder.optional(JsonDecoder.string),
        dateOpened: JsonDecoder.optional(JsonDecoder.string),
        disclaimerText: JsonDecoder.optional(JsonDecoder.string),
        displayName: JsonDecoder.string,
        displayFields: JsonDecoder.optional(
            JsonDecoder.array(accountPositionDisplayFieldDecoder, 'AccountPositionDisplayField[]')
        ),
        fundManagerName: JsonDecoder.optional(JsonDecoder.string),
        id: JsonDecoder.string,
        fundId: JsonDecoder.optional(JsonDecoder.string),
        marketValue: JsonDecoder.number,
        name: JsonDecoder.string,
        positionStatus: JsonDecoder.optional(JsonDecoder.string),
        priceAsOf: JsonDecoder.optional(JsonDecoder.string),
        priceType: JsonDecoder.optional(JsonDecoder.string),
        readOnly: JsonDecoder.optional(JsonDecoder.boolean),
        recordedAt: JsonDecoder.optional(JsonDecoder.string),
        securityCustomName: JsonDecoder.optional(JsonDecoder.string),
        securityDisplayName: JsonDecoder.optional(JsonDecoder.string),
        securityId: JsonDecoder.optional(JsonDecoder.string),
        securityName: JsonDecoder.optional(JsonDecoder.string),
        symbol: JsonDecoder.string,
        unitPrice: JsonDecoder.optional(JsonDecoder.number),
        units: JsonDecoder.optional(JsonDecoder.number)
    },
    'AccountPosition'
);

export const accountSecurityDecoder = JsonDecoder.object<AccountSecurity>(
    {
        id: JsonDecoder.string,
        displayName: JsonDecoder.optional(JsonDecoder.string),
        name: JsonDecoder.optional(JsonDecoder.string)
    },
    'AccountSecurity'
);

export const accountCustomFieldsDecoder = JsonDecoder.object<AccountCustomFields>(
    {
        lastMaintenanceDate: JsonDecoder.optional(JsonDecoder.string),
        externalCustodianId: JsonDecoder.optional(JsonDecoder.string),
        externalCustodianName: JsonDecoder.optional(JsonDecoder.string)
    },
    'AccountCustomFields'
);

export const accountDocumentDecoder = JsonDecoder.object<AccountDocument>(
    {
        id: JsonDecoder.string,
        accountId: JsonDecoder.string,
        type: JsonDecoder.string,
        subtype: JsonDecoder.string,
        displayName: JsonDecoder.string,
        filename: JsonDecoder.string,
        url: JsonDecoder.string,
        version: JsonDecoder.string,
        documentDate: JsonDecoder.string,
        securityName: JsonDecoder.optional(JsonDecoder.string),
        contactName: JsonDecoder.optional(JsonDecoder.string),
        thumbnail: JsonDecoder.optional(JsonDecoder.string),
        download: JsonDecoder.optional(JsonDecoder.boolean),
        name: JsonDecoder.optional(JsonDecoder.string),
        userUploaded: JsonDecoder.optional(JsonDecoder.boolean)
    },
    'AccountDocument'
);

export const accountDocumentsDecoder = JsonDecoder.array<AccountDocument>(
    accountDocumentDecoder,
    'AccountDocument[]'
);

export const accountDocumentPayloadDecoder = JsonDecoder.object<AccountDocumentPayload>(
    {
        id: JsonDecoder.string,
        options: JsonDecoder.succeed
    },
    'accountDocumentPayload'
);

export const accountDocumentsFiltersDecoder = JsonDecoder.array(
    JsonDecoder.object<AccountDocumentsFilter>(
        {
            label: JsonDecoder.string,
            key: JsonDecoder.string,
            queues: JsonDecoder.optional(filterListDecoder)
        },
        'AccountDocumentsFilter'
    ),
    'AccountDocumentsFilter[]'
);

export const accountOrdersFiltersDecoder = JsonDecoder.array(
    JsonDecoder.object<AccountOrdersFilter>(
        {
            label: JsonDecoder.string,
            id: JsonDecoder.string,
            index: JsonDecoder.number,
            queues: JsonDecoder.optional(filterListDecoder)
        },
        'AccountOrdersFilter'
    ),
    'AccountOrdersFilter[]'
);

export const accountDecoder = JsonDecoder.object<Account>(
    {
        accountNumber: JsonDecoder.optional(JsonDecoder.string),
        accountOwnershipType: JsonDecoder.optional(JsonDecoder.string),
        accountType: JsonDecoder.string,
        accountTypeName: JsonDecoder.optional(JsonDecoder.string),
        contactNames: JsonDecoder.optional(JsonDecoder.string),
        custodianName: JsonDecoder.optional(JsonDecoder.string),
        dateClosed: JsonDecoder.optional(JsonDecoder.string),
        dateOpened: JsonDecoder.optional(JsonDecoder.string),
        displayName: JsonDecoder.optional(JsonDecoder.string),
        entity: JsonDecoder.optional(investorDecoder).map(entity => entity || ({} as Investor)),
        // Left `undefined` when absent rather than defaulted to [], unlike `investors` just
        // below: an advisors-api that predates the fix sends no `entities` at all, and an
        // empty array there is indistinguishable from "this account has no entity" -- which
        // would hide the one `entity` such a response does carry. entitiesOf() does the
        // fallback, and needs to be able to tell the two apart.
        entities: JsonDecoder.optional(JsonDecoder.array<Investor>(investorDecoder, 'Investor[]')),
        id: JsonDecoder.optional(JsonDecoder.string),
        investors: JsonDecoder.optional(
            JsonDecoder.array<Investor>(investorDecoder, 'Investor[]')
        ).map(investors => investors || ([] as Investor[])),
        name: JsonDecoder.optional(JsonDecoder.string),
        customName: JsonDecoder.optional(JsonDecoder.string),
        ownershipType: JsonDecoder.optional(JsonDecoder.string),
        totalMarketValue: JsonDecoder.optional(JsonDecoder.number),
        totalCashValue: JsonDecoder.optional(JsonDecoder.number),
        isTransferOnDeath: JsonDecoder.optional(JsonDecoder.boolean),
        beneficiaries: JsonDecoder.optional(JsonDecoder.array(investorDecoder, 'Investor[]')),
        fundingInstructions: JsonDecoder.optional(fundingInstructionDecoder).map(
            fundingInstructions => fundingInstructions || ({} as FundingInstructions)
        ),
        distributions: JsonDecoder.optional(distributionsDecoder).map(
            distributions => distributions || ({} as Distributions)
        ),
        taxIdNumber: JsonDecoder.optional(JsonDecoder.string),
        ppmNumber: JsonDecoder.optional(JsonDecoder.string),
        customFields: JsonDecoder.optional(accountCustomFieldsDecoder),
        investments: JsonDecoder.optional(
            JsonDecoder.array<AccountInvestment>(AccountInvestmentDecoder, 'AccountInvestment[]')
        ).map(investments => investments || []),
        positions: JsonDecoder.optional(
            JsonDecoder.array<AccountPosition>(accountPositionDecoder, 'AccountPosition[]')
        ),
        hasAccountDocuments: JsonDecoder.optional(JsonDecoder.boolean),
        productNames: JsonDecoder.optional(JsonDecoder.string),
        firmName: JsonDecoder.optional(JsonDecoder.string),
        firmCRDNumber: JsonDecoder.optional(JsonDecoder.string),
        advisorName: JsonDecoder.optional(JsonDecoder.string),
        advisorCRDNumber: JsonDecoder.optional(JsonDecoder.string),
        advisorEmail: JsonDecoder.optional(JsonDecoder.string),
        advisorPhone: JsonDecoder.optional(JsonDecoder.string),
        dataSource: JsonDecoder.optional(JsonDecoder.string),
        securities: JsonDecoder.optional(
            JsonDecoder.array<AccountSecurity>(accountSecurityDecoder, 'AccountSecurity[]')
        )
    },
    'Account'
);

export const accountListDecoder = JsonDecoder.array<Account>(accountDecoder, 'Account[]');

// AccountRecord

export const accountRecordDecoder = JsonDecoder.object<AccountRecord>(
    {
        id: JsonDecoder.string,
        name: JsonDecoder.string,
        accountNumber: JsonDecoder.string,
        totalMarketValue: JsonDecoder.number,
        totalCashValue: JsonDecoder.number
    },
    'AccountRecord'
);

export const accountRecordListDecoder = JsonDecoder.array<AccountRecord>(
    accountRecordDecoder,
    'AccountRecord[]'
);

export const AccountsDocumentDecoder = JsonDecoder.array(
    JsonDecoder.object<AccountDocumentType>(
        {
            name: JsonDecoder.string,
            value: JsonDecoder.string
        },
        'AccountsDocument'
    ),
    'AccountsDocument[]'
);

// trade-platform/roadmap#321 -- editing an account and its contacts, and the history of it

/**
 * The per-field before/after inside a history entry.
 *
 * `from` and `to` are deliberately loose: the payload carries whatever column that save
 * touched, and a column can be text, a number or a boolean. A stricter decoder would reject
 * entries rather than render them, and a history that silently drops rows is worse than one
 * that shows a value it did not expect.
 */
export const accountFieldChangeDecoder = JsonDecoder.object<AccountFieldChange>(
    {
        from: JsonDecoder.optional(JsonDecoder.succeed),
        to: JsonDecoder.optional(JsonDecoder.succeed)
    },
    'AccountFieldChange'
);

export const accountChangeHistoryEntryDecoder = JsonDecoder.object<AccountChangeHistoryEntry>(
    {
        id: JsonDecoder.number,
        accountId: JsonDecoder.number,
        contactId: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.number)),
        contactDisplayName: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.string)),
        /*
         * A closed set, unlike ownershipType below, because this one really is a closed set:
         * aixdata.accounts_changed_history.ck_change_type enumerates it, so a value outside
         * the list cannot be stored.
         *
         * It still has to be kept in step with that constraint. This decoder sits inside
         * JsonDecoder.array, so ONE unrecognised entry rejects the whole list and the Change
         * History panel renders as a bare heading -- which is what would happen the moment the
         * API emitted its first 'contact_removed' if this were not here.
         */
        changeType: JsonDecoder.oneOf<AccountChangeType>(
            [
                JsonDecoder.isExactly('account_updated'),
                JsonDecoder.isExactly('contact_updated'),
                JsonDecoder.isExactly('contact_added'),
                JsonDecoder.isExactly('contact_removed')
            ],
            'AccountChangeType'
        ),
        changedAt: JsonDecoder.string,
        changedByDisplayName: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.string)),
        changedByEmail: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.string)),
        changes: JsonDecoder.dictionary(accountFieldChangeDecoder, 'AccountFieldChange map')
    },
    'AccountChangeHistoryEntry'
);

export const accountChangeHistoryDecoder = JsonDecoder.array(
    accountChangeHistoryEntryDecoder,
    'AccountChangeHistoryEntry[]'
);

export const accountOwnershipTypeDecoder = JsonDecoder.object<AccountOwnershipType>(
    {
        accountType: JsonDecoder.string,
        displayName: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.string)),
        headerName: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.string)),
        /*
         * A plain string, NOT a union of the three values this UI acts on.
         *
         * `aixdata.account_type_map.ownership_type` is an unconstrained varchar(255), and
         * `get_account_types` returns COALESCE(ownership_type_override, ownership_type) where
         * the override is a second unconstrained varchar. aix-data already ships rows outside
         * the three -- the SEI plan types carry 'plan' and 3_38_investment_manager carries
         * '3_38' -- and `upsert_account_type_group` moves an account type into the `default`
         * group with no schema change at all.
         *
         * A oneOf here sits inside `JsonDecoder.array` below, so ONE unexpected value rejects
         * the whole list rather than the one entry, and the Ownership dropdown renders with no
         * options at all. Narrowing belongs at the point of use: `missingContactNotice` just
         * compares against 'entity' and 'joint', so a value it does not recognise correctly
         * produces no notice instead of taking the list down.
         */
        ownershipType: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.string)),
        sequence: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.number)),
        disabled: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.boolean)),
        tooltip: JsonDecoder.optional(JsonDecoder.nullable(JsonDecoder.string))
    },
    'AccountOwnershipType'
);

export const accountOwnershipTypesDecoder = JsonDecoder.array(
    accountOwnershipTypeDecoder,
    'AccountOwnershipType[]'
);

export const accountEditAckDecoder = JsonDecoder.object<AccountEditAck>(
    {
        updated: JsonDecoder.boolean,
        changed: JsonDecoder.boolean
    },
    'AccountEditAck'
);

export const accountContactCreatedAckDecoder = JsonDecoder.object<AccountContactCreatedAck>(
    {
        created: JsonDecoder.boolean,
        contactId: JsonDecoder.number
    },
    'AccountContactCreatedAck'
);

/**
 * No `changed` flag, unlike the edit ack: a removal that returned 200 always recorded a
 * change-history row, because aixdata raises for every case where it did not.
 */
export const accountContactRemovedAckDecoder = JsonDecoder.object<AccountContactRemovedAck>(
    {
        removed: JsonDecoder.boolean
    },
    'AccountContactRemovedAck'
);
